import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Vehicle, VehicleFilters } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { VehicleListResponse } from '../../../types/Vehicle';
import VehiclesTable from '../components/VehiclesTable';
import VehicleFiltersComponent from '../components/VehicleFilters';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { Button, Spinner, PageHeader } from '@/components/ui';

const VehiclesView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [filters, setFilters] = useState<VehicleFilters>({
    currentStatus: '',
    serviceArea: '',
    vehicleClassification: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filterOptions, setFilterOptions] = useState<{
    currentStatuses: string[];
    serviceAreas: { id: number; name: string; code?: string }[];
    vehicleClassifications: string[];
  }>({
    currentStatuses: [],
    serviceAreas: [],
    vehicleClassifications: []
  });
  const { hasPermission } = usePermissions();
  const canCreateVehicle = hasPermission('vehicle:create');

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20');
    const filtersFromUrl: VehicleFilters = {
      currentStatus: searchParams.get('currentStatus') || '',
      serviceArea: searchParams.get('serviceArea') || '',
      vehicleClassification: searchParams.get('vehicleClassification') || '',
      search: searchParams.get('search') || ''
    };
    
    setPagination(prev => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
    setFilters(filtersFromUrl);
  }, [searchParams]);

  useEffect(() => {
    fetchVehicles();
  }, [pagination.page, pagination.pageSize, filters, sortBy, sortDir]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const options = await vehicleService.getVehicleFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const updateUrlParams = (newPagination?: Partial<typeof pagination>, newFilters?: VehicleFilters) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }
    
    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    
    setSearchParams(params);
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response: VehicleListResponse = await vehicleService.getVehiclesPaginated(
        pagination.page,
        pagination.pageSize,
        { ...filters, ...(sortBy ? { sortBy, sortOrder: sortDir } : {}) }
      );
      setVehicles(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    navigate(`/vehicles/${vehicle.id}`);
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters: VehicleFilters) => {
    setFilters(newFilters);
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ page: 1, pageSize });
  };

  return (
    <div className="tw-w-full tw-p-4">
      <PageHeader
        title="Vehicles"
        actions={[
          {
            key: 'add',
            label: 'Add Vehicle',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateVehicle,
            onClick: () => navigate('/vehicles/new'),
          },
        ]}
      />

      <VehicleFiltersComponent
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
      />

      {loading ? (
        <div className="tw-text-center tw-py-5">
          <Spinner role="status">
            <span className="tw-sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <VehiclesTable
            vehicles={vehicles}
            onSelectVehicle={handleSelectVehicle}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default VehiclesView;

